import { showAlert } from '../core/alerts.js';
import { validateCheckoutWarehouseSplit } from './checkout-warehouse-split.js';
import { validateOrderRows, renameOrderRows } from './validation.js';
import {
    getItemInventory,
    getMultiItemInventory,
    customOrderText,
    dropStaleOrderRowInventory,
} from './inventory.js';
import { showModal, hideModal } from '../core/bootstrap-helpers.js';
import { addCsrfField } from '../core/csrf.js';
import { getElementByHashId } from '../core/utilities.js';
import { displayToastrAlert } from '../ui/toastr-alert.js';
import { withViewAs } from '../ui/view-as-links.js';
import { ajaxError } from '../ui/feedback.js';
import { initQuickOrderModal } from '../quick-order/modal.js';
import {
    rememberReorderedRowIdentity,
    syncRowWarehouseIdentifiers,
} from './row-identity.js';

export { validateOrderRows, renameOrderRows } from './validation.js';
export { customOrderText };

export var _OrderItems = {};
export var _InstantOrderForm = { url: undefined, data: new FormData() };

/**
 * Surface a failed line-item removal.
 *
 * Every rejection path in Order::remove_item_from_order() answers 200 with a body
 * of false, so callers have to branch on the body rather than on the status. Before
 * this the handlers reloaded unconditionally and a refusal was indistinguishable
 * from a successful remove.
 */
function reportRemoveItemFailed() {
    showAlert({
        title: 'That item could not be removed.',
        text: 'Please reload the page and try again. If it keeps happening, contact us and we can remove it for you.',
        icon: 'error',
    });
}

function postInstantOrder(orderId, poItems, poNumber, isPriceMatch) {
    var action = '/whs/submit_purchase_order';
    var $auInput = $('#au');
    if ($auInput.length) {
        action += '?au=' + $auInput.val();
    }
    var $form = $('<form>', { method: 'post', action: action });
    var addField = function (name, value) {
        $form.append(
            $('<input>', { type: 'hidden', name: name, value: value })
        );
    };
    $.each(poItems || {}, function (rowId, qty) {
        addField('po_items[' + rowId + ']', qty);
    });
    addField('po_number', poNumber);
    addField('pricematch', isPriceMatch ? 'pricematch' : '');
    addField('shipment_type', 'DELIVERY');
    addField('order_notes[' + orderId + ']', '');
    addField('shipping_estimates_serialized', '');
    // This is a real form navigation, not an AJAX call, so the header hooks in core/csrf.js
    // never see it - and the form was built here, so it has no form_open() field either. The
    // token has to be added by hand or the csrf filter refuses the POST. Issue #555.
    addCsrfField($form);
    $form.appendTo('body');
    $form[0].submit();
}

function loadQuickOrderModalLinesFromOrder($orderModal, orderId) {
    if (!orderId) {
        return;
    }
    var viewAsAccount = $('#vaa').length ? '?au=' + $('#vaa').val() : '';
    var $table = $orderModal.find('.multi-order-table tbody');
    var $defaultRow = $table.find('.default-row');
    var appendRows = $orderModal.find('#appendReorder').length;

    $orderModal.find('#appendReorder').remove();

    $.ajax({
        error: ajaxError('Could not load the order items.'),
        url: '/order/get_order_items/' + orderId + viewAsAccount,
        type: 'get',
        dataType: 'json',
        beforeSend: function () {
            $orderModal
                .find('[type="submit"], .instant-order')
                .prop('disabled', true);
        },
        success: function (data) {
            if (data.length > 0) {
                if (!appendRows) {
                    $table.find('tr').not('.default-row').remove();
                }

                for (var i = 0; i < data.length; i++) {
                    var item = data[i];
                    // Record of the loaded lines. get_order_items returns flat rows,
                    // so brand settings are not in here - the inventory lookup gets
                    // them from catalog/ajaxGetBrand instead.
                    _OrderItems[item.WHLineCode + '-' + item.PartNumber] =
                        item;
                    var $row = $table.find('.default-row').clone();
                    $row.attr('data-whlc', item.WHLineCode).attr(
                        'data-whsn',
                        item.WHStockNumber
                    );
                    $row.find('.multi-order-brand').val(item.AAIABrandCode);
                    $row.find('.multi-order-part-number').val(item.PartNumber);
                    // The two attributes above are the stored LINE's, not the row's: the
                    // moment the buyer retypes this row they describe a different part.
                    // Record what they belong to so they can be dropped on an edit and put
                    // back on an undo. #700
                    rememberReorderedRowIdentity($row);
                    $row.find('.multi-order-remove').attr(
                        'data-item-id',
                        item.RowID
                    );
                    $row.removeClass('default-row');
                    $row.find('.multi-order-quantity')
                        .val(item.Quantity)
                        .prop('disabled', false);
                    $defaultRow.before($row);
                }

                getMultiItemInventory($table);

                renameOrderRows($orderModal.find('.multi-order-table'));
            }
        },
    });
}

/**
 * Re-check stock and pricing for the lines already in the modal. The rendered
 * numbers came from whenever the modal was last open, so on reopen they are
 * stale - one batched request replaces all of them.
 */
function refreshQuickOrderInventory($orderModal) {
    getMultiItemInventory($orderModal.find('.multi-order-table'));
}

function isExplicitSingleOrderForm($form) {
    var v = $form.attr('data-single-order');
    if (v === undefined || v === null) {
        return false;
    }
    var s = String(v).toLowerCase();
    return String(v) === '1' || s === 'true';
}

/**
 * Multi ordering: open #orderItemModal* so the user picks an order. Modal nodes sit
 * next to browse cards; move to document.body while shown so layout/stacking does
 * not hide the dialog.
 */
function openMultiOrderItemModal($form) {
    var $product = $form.closest('.product-group');
    var quantity = $form.find('.quantity-input').val();
    var orderModalEl = getElementByHashId($form.attr('data-order-modal'));
    var $orderModal = orderModalEl ? $(orderModalEl) : $();

    if (!$orderModal.length) {
        displayToastrAlert(
            'error',
            'Unable to open the order dialog for this product. Try refreshing the page.',
            true
        );
        return;
    }

    var $orderForm = $orderModal.find('form');
    var $quantity = $orderForm
        .find('.multi-order-quantity, .quantity-input')
        .first();

    // Copy the card quantity in BEFORE asking for inventory. getItemInventory()
    // snapshots this input on entry and rewrites it when the stock response lands, so
    // a copy made after the call is clobbered - the input renders with only a
    // placeholder, so the snapshot was empty and the callback reset the user's number
    // to 1. Set first and the snapshot is their value; an empty or invalid card
    // quantity still falls through to the val('1') default there. Issue #576.
    if ($quantity.length) {
        $quantity.val(quantity);
    }

    getItemInventory($orderModal, {
        aaia: $product.attr('data-aaia'),
        partnumber: $product.attr('data-pn'),
        gtin: $product.attr('data-gtin'),
        linecode: $product.attr('data-whlc'),
        stocknumber: $product.attr('data-whsn'),
    });

    if ($product.find('.no-stock').length && $quantity.length) {
        $quantity.removeAttr('max');
    }

    var modalEl = $orderModal[0];
    if (modalEl && modalEl.parentNode !== document.body) {
        if (!$orderModal.data('paceOrderModalShelf')) {
            $orderModal.data('paceOrderModalShelf', $orderModal.parent());
        }
        document.body.appendChild(modalEl);
    }

    showModal($orderModal);
}

// One binder per concern, called in the original registration order by initOrdering() below.
// This was a single ~845-line function registering every ordering handler inline, where the
// interaction between stopImmediatePropagation, preventDefault and registration order was
// impossible to follow. Nothing about the handlers changed - only where they live. Issue #302.

// Puts an order-item modal back on the shelf element it was moved from when it closes.
function bindOrderItemModalReparent() {
    $(document).on(
        'hidden.bs.modal.paceOrderItemReparent',
        '[id^="orderItemModal"]',
        function () {
            var $m = $(this);
            var $shelf = $m.data('paceOrderModalShelf');
            if ($shelf && $shelf.length && document.body.contains($shelf[0])) {
                $shelf[0].appendChild($m[0]);
            }
        }
    );

    // Browse grid: ensure add-to-order opens the picker even if submit handling misbehaves.
    $(document).on(
        'click',
        '.order-form:not(.ajax-form) input[type="submit"].product-order, .order-form:not(.ajax-form) .btn-add-to-order[type="submit"]',
        function (e) {
            var $form = $(this).closest('.order-form');
            if (isExplicitSingleOrderForm($form)) {
                return;
            }
            e.preventDefault();
            e.stopImmediatePropagation();
            openMultiOrderItemModal($form);
        }
    );

}

function bindOrderFormSubmit() {
    // Delegated: browse shell replaces .browse-main-col via AJAX without re-running
    // initOrdering; direct .submit() would not bind new product cards.
    $(document).on('submit', '.order-form', function (e) {
        if (isExplicitSingleOrderForm($(this))) {
            return true;
        }

        e.preventDefault();
        e.stopPropagation();

        openMultiOrderItemModal($(this));
    });

}

// Stops Enter in the order modal submitting the form by accident.
function bindOrderModalKeyGuard() {
    $('#orderModal form').on('keyup keypress', function (e) {
        var keyCode = e.keyCode || e.which;

        if (keyCode == 13) {
            e.preventDefault();
        }
    });

}

function bindInstantOrderOpen() {
    // Delegated: .instant-order buttons live in product cards the browse shell
    // replaces via AJAX; a direct .click() would not bind re-rendered cards, so
    // the modal opens (via data-bs-toggle) but its body/URL never get populated.
    $(document).on('click', '.instant-order', function (e) {
        e.preventDefault();

        var $form = $(this).closest('form');
        var $submitButton = $form.find('[type="submit"]');
        var poIsRequired = $(this).attr('data-po-is-required');

        // Reset accumulated form data so reopening the modal doesn't double-append.
        _InstantOrderForm.data = new FormData();

        if (!$form.find('input[name="InstantOrder"]').length) {
            $form.prepend(
                '<input type="hidden" name="InstantOrder" value="1" />'
            );
        }

        var are_fields = false;

        if (poIsRequired === '1') {
            $('#po-field').removeClass('d-none');
            $('#po-num-label').addClass('required');
            $('#po-required-tag').removeClass('d-none');
            are_fields = true;
        } else {
            $('#po-field').addClass('d-none');
            $('#po-num-label').removeClass('required');
            $('#po-required-tag').addClass('d-none');
        }

        if ($(this).attr('data-price-match')) {
            $('#is-price-match').val('1');
            $('#price-match-fields').removeClass('d-none');
            if ($(this).attr('email-only')) {
                $('#is-email-only-price-match').val('1');
                $('#instantOrderModalLabel').html('Price Match Message');
                $('#inst-order-btn-label').html('Submit Price Match Message');
                $('#po-field').addClass('d-none');
                $('#po-num-label').removeClass('required');
                $('#po-required-tag').addClass('d-none');
            } else {
                $('#instantOrderModalLabel').html('Price Match Order');
                $('#inst-order-btn-label').html('Submit Price Match Order');
            }
            are_fields = true;
        } else {
            $('#instantOrderModalLabel').html('Instant Order');
            $('#is-price-match').val('0');
            $('#price-match-fields').addClass('d-none');
        }

        if (are_fields) {
            $('#are-you-sure').addClass('d-none');
        } else {
            $('#are-you-sure').removeClass('d-none');
        }

        var inv = parseInt($form.find('input[name="inv"]').val());
        var qty = parseInt($form.find('input[name="qty"]').val());
        if (qty > inv && !$(this).attr('email-only')) {
            $('#inst-modal-backorder-notice').removeClass('d-none');
            $('#inst-modal-backordered-num').html(qty - inv);
        } else {
            $('#inst-modal-backorder-notice').addClass('d-none');
            $('#inst-modal-backordered-num').html(0);
        }

        if ($(this).attr('email-only')) {
            _InstantOrderForm.url = $form
                .attr('action')
                .replace('add_item_to_order', 'emailonly_pricematch');
        } else {
            _InstantOrderForm.url = $form.attr('action');
        }
        _InstantOrderForm.data.append('qty', qty);
        _InstantOrderForm.data.append('inv', inv);
    });

}

// The instant-order submit: validation, the AJAX post, price-match handoff and the redirect.
function bindInstantOrderSubmit() {
    $(document).on('click', '.btn-submit-instant-order', function (e) {
        if (
            typeof _InstantOrderForm.url !== 'string' ||
            typeof _InstantOrderForm.data !== 'object'
        ) {
            return console.error(
                '_InstantOrderForm',
                'Invalid form data for instant order'
            );
        }

        var complete = true;
        var $button = $(this);
        var isPriceMatch = $('#is-price-match').val() === '1';
        var isEmailOnly = $('#is-email-only-price-match').val() === '1';
        var poIsRequired =
            $button.attr('data-po-required') === '1' && !isEmailOnly;
        var originalButtonText = $button.text();

        var $inputPoNumber = $('#instantOrderPoNumber');
        var poNumber = $inputPoNumber.val();
        if (typeof poNumber !== 'string') {
            poNumber = '';
        }
        // Trimmed once, here, and otherwise sent exactly as typed.
        //
        // The trim is up front so a whitespace-only field is the same "typed nothing" as an
        // empty one everywhere below, rather than being carried through as spaces. It is also
        // the only normalisation, and it is the one checkPONumber() applies - which is the
        // point, because the two submission routes have to agree about what a PO number is.
        //
        // This line used to replace every space with an underscore, a fossil of PACE-1: there
        // the same variable was interpolated into a redirect PATH,
        // `/order/submit/<id>/instant/<poNumber>[/pricematch]`, and PACE-1 wrapped it in
        // encodeURIComponent() on the same line. PACE-2 kept the underscore half and dropped
        // the encoding half, and PR #1010 (#943) then deleted the path shape altogether. So
        // the substitution was protecting a URL that no longer exists while still rewriting
        // the customer's own reference: `PO 1234` reached the distributor's ERP as `PO_1234`,
        // disagreeing with the number on their paperwork with nobody told. checkPONumber() is
        // the guard now, and it keeps a space byte for byte (#1019).
        poNumber = poNumber.trim();
        if (poNumber.length) {
            _InstantOrderForm.data.append('PurchaseOrderNumber', poNumber);
        } else if (poIsRequired) {
            complete = false;
        }

        if (isPriceMatch) {
            var compName = $('#priceMatchCompetitorName').val().trim();
            var prcMtch = $('#priceMatchPrice').val().trim();
            var imgVal = $('#priceMatchImage').val();
            if (
                compName.length == 0 ||
                prcMtch.length == 0 ||
                imgVal.length == 0
            ) {
                complete = false;
            }
        }

        if (!complete) {
            showAlert({
                title: 'You must enter a value for all required fields to continue.',
                icon: 'warning',
                confirmButtonColor: '#b20000',
                confirmButtonText: 'OK',
            });
            return false;
        }

        if (
            parseInt($('#inst-modal-backordered-num').html()) > 0 &&
            !$('#inst-modal-backorder-ack').prop('checked')
        ) {
            showAlert({
                title: 'You must check the box acknowledging backordered items to continue.',
                icon: 'warning',
                confirmButtonColor: '#b20000',
                confirmButtonText: 'OK',
            });

            return false;
        }

        _InstantOrderForm.data.append('instant', 1);

        $.ajax({
            url: _InstantOrderForm.url,
            data: _InstantOrderForm.data,
            type: 'post',
            processData: false,
            contentType: false,
            dataType: 'json',
            beforeSend: function () {
                $button.prop('disabled', true);

                $button
                    .html(
                        '<i class="fa fa-circle-o-notch fa-spin"></i><span class="visually-hidden">Loading...</span> Submitting...'
                    )
                    .prop('disabled', true);
            },
            complete: function (response) {
                if (!response.hasOwnProperty('responseJSON')) {
                    console.log(response);
                    return false;
                }

                $button.text(originalButtonText).prop('disabled', true);

                var data = response.responseJSON;

                if (data.error) {
                    console.error('Could not submit order', data);
                    displayToastrAlert('error', data.error);
                    $button.text(originalButtonText).prop('disabled', false);
                    return false;
                }

                // No substitute PO number for a price match. This used to read
                // `if (poNumber == '' && isPriceMatch) { poNumber = 'pricematch'; }`,
                // which existed only because PACE-1 built the confirmation redirect as
                // `/order/submit/<id>/instant/<poNumber>/pricematch` and CodeIgniter drops
                // empty segments - an empty PO collapsed the two, so `pricematch` arrived
                // as $ponum. PR #1010 (#943) removed that shape: the flag now travels as
                // its own `pricematch` post field into flash('submitted_pricematch'), so
                // the placeholder guarded nothing and was writing the literal string
                // `pricematch` into PurchaseOrders.Number and sending it to the ERP as the
                // customer's reference. The price-match email does not read the PO number
                // at all - sendPriceMatchMessage() branches on session_value('pm')
                // ['PriceMatchImage'] - so an empty one still sends it (#1019).
                var $auInput = $('#au');

                if (isPriceMatch) {
                    var priceMatchFormData = new FormData();
                    priceMatchFormData.append('IsPriceMatch', '1');
                    priceMatchFormData.append(
                        'IsEmailOnly',
                        isEmailOnly ? '1' : '0'
                    );
                    // Appended even when empty: Order::price_match_setup() reads
                    // $_POST['PurchaseOrderNumber'] with no null coalesce, and a missing key
                    // is an E_WARNING that Config/Boot/production.php turns into a 500 on the
                    // price-match relay. An empty VALUE is fine; an absent KEY is not.
                    priceMatchFormData.append('PurchaseOrderNumber', poNumber);
                    priceMatchFormData.append('CompetitorName', compName);
                    priceMatchFormData.append('MatchPrice', prcMtch);
                    priceMatchFormData.append(
                        'PriceMatchImage',
                        $('#priceMatchImage')[0].files[0]
                    );
                    if (!isEmailOnly) {
                        priceMatchFormData.append('OrderRowID', data.order_id);
                    } else {
                        priceMatchFormData.append('aaia', data.aaia);
                        priceMatchFormData.append('pn', data.pn);
                    }
                    $.ajax({
                        url:
                            '/order/price_match_setup' +
                            ($auInput.length ? '?au=' + $auInput.val() : ''),
                        data: priceMatchFormData,
                        type: 'post',
                        processData: false,
                        contentType: false,
                        dataType: 'json',
                        // price_match_setup echoes 1 on success and 0 when validation
                        // or the move fails. This was read from `complete`, which fires
                        // for failures too - so a rejected upload still showed the
                        // success modal (or submitted the order) while session_value('pm')
                        // was never set and the evidence was silently dropped.
                        success: function (pm_response) {
                            if (!pm_response) {
                                $button
                                    .text(originalButtonText)
                                    .prop('disabled', false);
                                alert('Failed to submit price match');
                                return;
                            }
                            if (isPriceMatch && isEmailOnly) {
                                hideModal($button.closest('.modal'));
                                showModal($('#pmEmailOnlyModal'));
                            } else {
                                postInstantOrder(
                                    data.order_id,
                                    data.po_items,
                                    poNumber,
                                    true
                                );
                            }
                        },
                        error: function (pm_response) {
                            console.log(pm_response.responseText);
                            $button
                                .text(originalButtonText)
                                .prop('disabled', false);
                            alert('Failed to submit price match');
                        },
                    });
                } else {
                    postInstantOrder(
                        data.order_id,
                        data.po_items,
                        poNumber,
                        false
                    );
                }
            },
            error: function () {
                $button.text(originalButtonText).prop('disabled', false);
                alert('Failed to submit instant order');
            },
        });
    });

}

function bindQuickOrderButton() {
    $('.btn-quick-order').click(function () {
        var $orderModal = $('#orderModal');
        var $autocomplete = $(this)
            .closest('form')
            .find('.autocomplete-search');
        var $table = $orderModal.find('table');

        if ($autocomplete.val() != undefined && $autocomplete.val().trim().length > 0) {
            var $defaultRow = $table.find('.default-row');
            var $newRow = $defaultRow.clone();
            $newRow.insertBefore($defaultRow);
            $newRow
                .find('.multi-order-part-number')
                .val($autocomplete.val().trim());
            $newRow.removeClass('default-row');
            renameOrderRows($table);
        }
    });

}

function bindOrderDropdown() {
    $('.order-dropdown').change(function () {
        var $orderNameField = $(this)
            .closest('form')
            .find('.js-input-order-name');
        var viewAsAccount = $('#vaa').length ? '?au=' + $('#vaa').val() : '';

        if ($(this).val() === '') {
            $orderNameField.val('');
            $orderNameField.closest('.mb-3').show();
        } else {
            $orderNameField.closest('.mb-3').hide();

            var orderId = $(this).val();
            var $orderModal = $('#orderModal');
            loadQuickOrderModalLinesFromOrder($orderModal, orderId);
        }
    });

}

function bindOrderModalShown() {
    // One inventory fetch per open. loadQuickOrderModalLinesFromOrder() ends in its
    // own getMultiItemInventory() call, so the refresh is the other branch of the
    // same decision rather than a second request on top of it.
    $('#orderModal').on('shown.bs.modal', function () {
        var $modal = $(this);
        window.setTimeout(function () {
            var $form = $modal.find('form');
            var $tbody = $form.find('.multi-order-table tbody');
            var hasRows = $tbody.find('tr').not('.default-row').length > 0;

            if (!hasRows) {
                if ($form.attr('data-single-order') !== '1') {
                    return;
                }
                var defaultId = $form.attr('data-default-order-rowid');
                if (!defaultId) {
                    return;
                }
                loadQuickOrderModalLinesFromOrder($modal, defaultId);
                return;
            }

            refreshQuickOrderInventory($modal);
        }, 0);
    });

}

// Two things a row carries must not survive the buyer re-identifying it: the warehouse
// identifiers it was stamped with (ordering/row-identity.js explains why, and why they come
// back if the edit is undone) and the stock and price already rendered into it, which
// describe the part the last lookup asked about rather than the one the row now names
// (#706). Neither is re-derived on its own - nothing re-requests inventory on a keystroke -
// so both have to be answered here, on the edit itself.
//
// Delegated from the table rather than from document, and that is load-bearing rather than a
// style choice: quick-order/modal.js binds its .multi-order-brand handler on #orderModal and
// calls getItemInventory() from it synchronously, and that call reads the two attributes off
// the row. A native event reaches the table before it reaches #orderModal, so this has always
// run by the time the lookup is built. Bound on document - the house style for the row
// handlers below - it would run last, and a brand change on a reordered row would still send
// the old part.
function bindOrderRowIdentityChanges() {
    $('.multi-order-table').on(
        'input.paceRowIdentity change.paceRowIdentity',
        '.multi-order-part-number, .multi-order-brand',
        function () {
            var $row = $(this).closest('tr');

            syncRowWarehouseIdentifiers($row);
            // After the sync, so a lookup fired by a handler further out builds its request
            // from identifiers that already match the row.
            dropStaleOrderRowInventory($row);
        }
    );
}

// Row-level actions inside the multi-item order table: remove, and the alternate-part swap.
function bindMultiOrderTableRows() {
    $(document)
        .on('click', '.multi-order-remove', function (e) {
            var $self = $(this);
            var $row = $self.closest('tr');
            var $table = $self.closest('.multi-order-table');
            var itemID = $self.attr('data-item-id');

            if (typeof itemID !== typeof undefined && itemID !== false) {
                showAlert({
                    title: 'Are you sure you want to remove this item?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#b20000',
                    confirmButtonText: 'Remove',
                }).then((result) => {
                    if (result.isConfirmed) {
                        $.ajax({
                            // Relative + withViewAs() for the same reasons as the
                            // cart handler below; the leading slash also broke any
                            // site deployed under a base_url with a path segment.
                            //
                            // Two distinct failures, two reporters: ajaxError() takes
                            // the transport error like every other call here, and the
                            // success branch below takes the refusal, which arrives as
                            // a 200 with a body of false and so never reaches error:.
                            error: ajaxError(
                                'Could not remove that item. Please refresh and try again.'
                            ),
                            url: withViewAs('order/remove_item_from_order'),
                            data: { ItemRowID: itemID },
                            type: 'post',
                            dataType: 'json',
                            success: function (removed) {
                                if (!removed) {
                                    reportRemoveItemFailed();
                                    return;
                                }
                                if (
                                    !$row.hasClass('default-row') &&
                                    $table.find('tr').length > 1
                                ) {
                                    $self.closest('tr').remove();
                                }

                                validateOrderRows($table);
                            },
                        });
                    }
                });
            } else {
                if (
                    !$row.hasClass('default-row') &&
                    $table.find('tr').length > 1
                ) {
                    $self.closest('tr').remove();
                }

                validateOrderRows($table);
            }
        })

        .on('click', '.btn-flag-order', function (e) {
            e.preventDefault();

            var $button = $(this);
            var $form = $button.closest('form');

            $.ajax({
                error: ajaxError('Could not update the order.'),
                url: $form.attr('action'),
                data: $form.serialize(),
                type: 'post',
                success: function () {
                    if (!$button.parent().parent().hasClass('order-buttons')) {
                        var $dot = $button.closest('tr').find('.flag');

                        if ($dot.hasClass('flagged')) {
                            $dot.removeClass('flagged').addClass('unflagged');
                        } else {
                            $dot.removeClass('unflagged').addClass('flagged');
                        }
                    }

                    if ($button.val() == 'Unflag for Purchase') {
                        $button
                            .removeClass('btn-secondary')
                            .addClass('btn-warning')
                            .val('Flag for Purchase');
                        $form.attr('action', 'order/ajaxFlagForPurchase');
                    } else {
                        $button
                            .removeClass('btn-warning')
                            .addClass('btn-secondary')
                            .val('Unflag for Purchase');
                        $form.attr('action', 'order/ajaxUnflagForPurchase');
                    }
                },
            });
        })

        .on('click', '.flag', function () {
            var $self = $(this);
            var url = $self.hasClass('flagged')
                ? 'order/ajaxUnflagForPurchase'
                : 'order/ajaxFlagForPurchase';

            var $auInput = $('#au');

            if ($auInput.length) {
                url += '?au=' + $auInput.val();
            }

            $.ajax({
                error: ajaxError('Could not update the order.'),
                url: url,
                data: { OrderRowID: $self.attr('data-id') },
                type: 'post',
                success: function () {
                    if ($self.hasClass('flagged')) {
                        $self
                            .removeClass('flagged')
                            .addClass('unflagged')
                            .attr('title', 'Flag for purchase');
                    } else {
                        $self
                            .removeClass('unflagged')
                            .addClass('flagged')
                            .attr('title', 'Unflag for purchase');
                    }
                },
            });
        });

}

function bindQuantityInputs() {
    $('.multi-order-quantity, .order-form input[type="number"]').on(
        'change keyup paste input',
        function () {
            if ($(this).val() <= 0) {
                $(this).val('1');
            } else if (
                typeof $(this).attr('max') !== typeof undefined &&
                $(this).attr('max') !== false &&
                $(this).val() >= parseInt($(this).attr('max'))
            ) {
                $(this).val($(this).attr('max'));
            }
        }
    );

}

function bindOrderItemQuantity() {
    $(document).on('change', '.order-item [data-order-qty]', function () {
        var $quantityElement = $(this);
        var $tableCell = $quantityElement.closest('td');
        var $maxQuantityElement = $tableCell.find('.po-max-qty');
        var $quantityLocationElement = $tableCell.find(
            '[data-order-qty-location]'
        );
        var $quantityError = $tableCell.find('.order-qty-error');
        var maxQuantity = null;

        if ($maxQuantityElement.length && $quantityLocationElement.length) {
            maxQuantity = parseInt($maxQuantityElement.text());

            if (!isNaN(maxQuantity)) {
                var totalUserQuantity = 0;

                $quantityElement
                    .closest('.order-item')
                    .find('[data-order-qty]')
                    .each(function () {
                        totalUserQuantity += parseInt($(this).val());
                    });

                if (totalUserQuantity != maxQuantity) {
                    $quantityError
                        .removeClass('d-none')
                        .parent()
                        .addClass('is-invalid');
                } else {
                    $quantityError
                        .addClass('d-none')
                        .parent()
                        .removeClass('is-invalid');
                }
            }
        } else if (
            isNaN($quantityElement.val()) ||
            $quantityElement.val() <= 0
        ) {
            $quantityElement.val('1');
        }

        if (
            typeof $quantityElement.attr('max') !== typeof undefined &&
            $quantityElement.attr('max') !== false &&
            $quantityElement.val() >= parseInt($quantityElement.attr('max')) &&
            !$quantityElement.closest('td').find('.quantity-tooltip').length
        ) {
            $quantityElement.val($quantityElement.attr('max'));

            $quantityElement
                .closest('td')
                .append(
                    '<span class="quantity-tooltip">Maximum ' +
                        $quantityElement.attr('max') +
                        '</span>'
                );

            setTimeout(function () {
                var $tooltip = $quantityElement
                    .closest('td')
                    .find('.quantity-tooltip');

                $tooltip.fadeOut(function () {
                    $tooltip.remove();
                });
            }, 1000);
        }

        // No total is recalculated here, deliberately. This handler is bound to
        // '.order-item [data-order-qty]', a selector NO view renders - grep app/Views: zero
        // occurrences - so it never fires, and it is left in place only because removing a dead
        // handler is a separate change from this one.
        //
        // Nothing is missing even if it did fire. order/view.inc renders the single-warehouse
        // quantity as a read-only <span class="order-item__qty-pill"> plus a hidden input, so
        // there is no editable quantity on that table to recalculate from. Multi-warehouse rows
        // have '.order-wh-qty', and checkout-warehouse-split.js totals those from its own
        // listeners, including the shipping-estimate input. Raised in review on #965.

        var submit_button_orig_label = $('#submitOrder').val();
        var submit_button_was_disabled = $('#submitOrder').is('[disabled]');
        $('#submitOrder')
            .val('Save Changes')
            .removeClass('btn-success')
            .removeAttr('disabled')
            .addClass('btn-info');

    });

}

// Only bound on the order page itself: rename, notes, PO number and the submit-order controls.
function bindOrderPageControls() {
    if ($('.order-submit').length && $('#submitOrder').length) {
        $('#edit-order-name').click(function () {
            var $self = $(this);
            var $orderName = $self.parent();
            $orderName
                .hide()
                .prev('#order-name-form')
                .css({ display: 'inline' })
                .find('.js-input-order-name')
                .val($orderName.find('span').text());
        });

        $('#order-name-form').submit(function (e) {
            e.preventDefault();
            var $form = $(this);
            var $orderName = $form.next('#order-name');

            $.ajax({
                error: ajaxError('Could not save the order name.'),
                url: $form.attr('action'),
                type: 'post',
                data: $form.serialize(),
                // success, not complete: complete also runs on a 500 or a dropped
                // connection, so the name was swapped into the page while the database
                // still held the old one. On failure the input stays visible to retry.
                success: function () {
                    $form.hide();
                    $orderName
                        .show()
                        .find('span')
                        .text($form.find('.js-input-order-name').val());
                },
            });
        });

        var submit_button_orig_label = $('#submitOrder').val();
        var submit_button_was_disabled = $('#submitOrder').is('[disabled]');
        $('.order-submit #OrderNotes').keyup(function () {
            $('#submitOrder')
                .val('Save Changes')
                .removeClass('btn-success')
                .removeAttr('disabled')
                .addClass('btn-info');
        });

        $('#submitOrder').click(function (e) {
            if ($(this).val() === 'Save Changes') {
                e.preventDefault();

                $('input[name="Quantity"]').each(function (i, input) {
                    var $form = $(input).closest('form');

                    $.ajax({
                        error: ajaxError('Could not save your changes.'),
                        url: $form.attr('action'),
                        data: $form.serialize(),
                        type: 'post',
                        beforeSend: function () {
                            $form
                                .parent()
                                .prepend(
                                    '<span class="badge bg-success">Saving</span>'
                                );
                        },
                        complete: function () {
                            $form.parent().find('.label').remove();
                        },
                    });
                });

                var $notesForm = $('#addNotes');

                $.ajax({
                    error: ajaxError('Could not save the notes.'),
                    url: $notesForm.attr('action'),
                    data: $notesForm.serialize(),
                    type: 'post',
                    beforeSend: function () {
                        $notesForm
                            .find('label')
                            .append(
                                ' <span class="badge bg-success">Saving</span>'
                            );
                    },
                    complete: function () {
                        $notesForm.find('label .label').remove();
                    },
                });

                $('#submitOrder')
                    .val(submit_button_orig_label)
                    .removeClass('btn-info')
                    .addClass('btn-success');
                if (submit_button_was_disabled) {
                    $('#submitOrder').attr('disabled', 'disabled');
                }
            }
        });

        $('.btn-empty-order').click(function (e) {
            e.preventDefault();
            var $self = $(this);

            showAlert({
                title:
                    'Are you sure you want to remove all items from this ' +
                    customOrderText +
                    '?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#b20000',
                confirmButtonText: 'Empty ' + customOrderText,
            }).then((result) => {
                if (result.isConfirmed) {
                    var $form = $self.closest('form');

                    $.ajax({
                        error: ajaxError('Could not empty the order.'),
                        url: $form.attr('action'),
                        data: $form.serialize(),
                        type: 'post',
                        success: function () {
                            window.location.href = window.location.href;
                        },
                    });
                }
            });
        });

        $('.btn-order-remove-item').click(function (e) {
            e.preventDefault();
            var $form = $(this).closest('form');
            var $row = $(this).closest('tr');
            var item = $(this).attr('data-item');

            showAlert({
                title: 'Are you sure you want to remove item ' + item + '?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#b20000',
                confirmButtonText: 'Remove',
            }).then((result) => {
                if (result.isConfirmed) {
                    $.ajax({
                        error: ajaxError('Could not remove that item.'),
                        url: $form.attr('action'),
                        data: $form.serialize(),
                        type: 'post',
                        success: function () {
                            // Same as above: this handler is bound to '.btn-order-remove-item',
                            // which no view renders either. The live remove control is
                            // '[data-order-remove-item]' (index.js:1202), and it reloads the page
                            // on success - so totals are refreshed by the server, not here.
                            $row.remove();
                        },
                    });
                }
            });
        });
    }

}

// The final Submit Purchase Order handler, including the double-submit guard from #442.
function bindReviewOrderSubmit() {
    $('#reviewOrderForm').submit(function (e) {
        if (!validateCheckoutWarehouseSplit()) {
            e.preventDefault();
            return false;
        }
        var $form = $(this);
        var $button = $form.find('#submitPO');
        var $poNumber = $('#PurchaseOrderNumber');
        var $backorderAgreement = $('#backorderAgreement');
        var valid = true;
        var tmp = $(
            '<input type="hidden" name="' +
                this.name +
                '" value="' +
                $button.val() +
                '" />'
        ).appendTo($form);

        if (
            typeof $poNumber.attr('required') !== typeof undefined &&
            $poNumber.attr('required') !== false &&
            $poNumber.val().length == 0
        ) {
            valid = false;
            $poNumber
                .css({ 'border-color': '#b20000', 'background-color': 'pink' })
                .attr('placeholder', 'required');
        } else {
            $poNumber
                .css({ 'border-color': '#cccccc', 'background-color': 'white' })
                .removeAttr('placeholder');
        }

        if ($backorderAgreement.length && !$backorderAgreement.is(':checked')) {
            valid = false;
            $('#backorderAgreementText').after(
                '<span id="backorderAgreementError" class="text-danger"> Required!</span>'
            );
        } else {
            $('#backorderAgreementError').remove();
        }

        if (!valid) {
            tmp.remove();
            e.preventDefault();
            return false;
        }

        // Valid: the native POST to /whs/submit_purchase_order is about to fire. That round trip
        // includes the warehouse API call, so it is slow and silent - and a second click submits
        // the order again, which nothing on the server dedupes. Issue #132.
        //
        // A flag rather than prop('disabled', true): a disabled input is not submitted, and while
        // Whs::submit_purchase_order() does not read submit_po_button today, silently dropping a
        // posted field is a trap for whoever adds that check. The flag also covers Enter-key
        // submits, and Bootstrap's .disabled class sets pointer-events: none, so the button stops
        // taking clicks while still posting its value.
        if ($form.data('paceSubmitting')) {
            e.preventDefault();
            return false;
        }

        // Another handler may already have cancelled this submit. #reviewOrderForm has an
        // inline onsubmit="return altLocationInv();", registered at parse time and so run
        // before this one: when a line exceeds inventory at the selected warehouse it
        // preventDefaults and opens #altLocationInvModal. Latching here would then leave
        // the form permanently guarded, and the modal's confirm - which removes the
        // onsubmit attribute and re-clicks #submitPO - would be refused by the check
        // above. Nothing would reach /whs/submit_purchase_order. Caught in review on #442.
        if (e.isDefaultPrevented()) {
            return;
        }

        $form.data('paceSubmitting', true);

        // form_submit() renders an <input type="submit">, so the label is its value
        $button
            .val('Submitting your order...')
            .addClass('disabled')
            .attr('aria-disabled', 'true');
    });

}

/**
 * Card-level delete on the checkout page: drop a whole order and its line items.
 *
 * The button only exists on a card the presenter cleared for it (more than one open order,
 * no line already on a purchase order), so there is no client-side eligibility check here -
 * Order::delete_order() is the authority and answers a refusal the same way its siblings do,
 * a 200 with a body of false. Hence the branch in success: rather than an unconditional
 * reload, which would look like the order had gone away when it had not.
 */
function bindOrderDeletion() {
    $(document).on('click', '[data-order-delete]', function () {
        var $self = $(this);
        var orderName = $self.attr('data-order-name') || '';
        // customOrderText is the site's word for an order ("Order", "Basket", ...). It is a
        // live binding set from #order-text by inventory.js and is '' until that has run, so
        // the fallback is not decorative - see the same guard in forms/ajax-form.js.
        var orderNoun = String(customOrderText || 'order').trim().toLowerCase() || 'order';

        showAlert({
            title: 'Delete this ' + orderNoun + '?',
            text: orderName
                ? orderName + ' and every item in it will be removed. This cannot be undone.'
                : 'Every item in it will be removed. This cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#b20000',
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            $.ajax({
                // withViewAs(): the endpoint gates on user()->AccountNumber, so without au=
                // a WD viewing a customer's cart is refused - and that refusal is a 200
                // with a body of false, which never reaches error:.
                error: ajaxError(
                    'Could not delete that order. Please refresh and try again.'
                ),
                url: withViewAs('order/delete_order'),
                type: 'post',
                dataType: 'json',
                data: {
                    OrderRowID: $self.attr('data-order-delete'),
                },
                success: function (deleted) {
                    if (deleted) {
                        window.location.reload();
                        return;
                    }
                    showAlert({
                        title: 'That order could not be deleted.',
                        text: 'It may already have been submitted, or it may be the only one left. Please reload the page and try again.',
                        icon: 'error',
                    });
                },
            });
        });
    });
}

function bindOrderItemRemoval() {
    $(document).on('click', '[data-order-remove-item]', function () {
        var $self = $(this);

        showAlert({
            title: 'Are you sure you want to remove this item?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#b20000',
            confirmButtonText: 'Remove',
        }).then((result) => {
            if (result.isConfirmed) {
                $.ajax({
                    // withViewAs(): the endpoint gates on user()->AccountNumber, so
                    // without au= a WD viewing a customer's cart is refused - and the
                    // refusal is a 200 with a body of false, so the reload below used
                    // to look like a successful remove that silently did nothing.
                    //
                    // Two distinct failures, two reporters: ajaxError() takes the
                    // transport error like every other call here, and the success
                    // branch takes that refusal, which never reaches error:.
                    error: ajaxError(
                        'Could not remove that item. Please refresh and try again.'
                    ),
                    url: withViewAs('order/remove_item_from_order'),
                    type: 'post',
                    dataType: 'json',
                    data: {
                        ItemRowID: $self.attr('data-order-remove-item'),
                    },
                    success: function (removed) {
                        if (removed) {
                            window.location.reload();
                            return;
                        }
                        reportRemoveItemFailed();
                    },
                });
            }
        });
    });

}

export function initOrdering() {
    // Reorder-row identity hygiene. Position here is incidental - see ordering/row-identity.js.
    bindOrderRowIdentityChanges();
    bindOrderItemModalReparent();
    bindOrderFormSubmit();
    bindOrderModalKeyGuard();
    bindInstantOrderOpen();
    bindInstantOrderSubmit();
    bindQuickOrderButton();
    bindOrderDropdown();
    bindOrderModalShown();
    bindMultiOrderTableRows();
    bindQuantityInputs();
    bindOrderItemQuantity();
    bindOrderPageControls();
    bindReviewOrderSubmit();
    bindOrderItemRemoval();
    bindOrderDeletion();
    initQuickOrderModal();
}
